import { DefaultButton, Modal, PrimaryButton, Stack } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RecommendedAudiences } from "src/components";
import { localize } from "src/l10n";
import { setConfirmPopup } from "src/popups/actions";
import { SpintrTypes } from "src/typings";
import { FormTokenizedObjectInput } from "src/ui/components/Forms";
import PopupHeader from "src/ui/components/PopupHeader";
import onRecommendedAudienceSelected from "src/utils/onRecommendedAudienceSelected";

interface IProps {
    onDismiss: () => void;
    onPostUpdate: (post: Spintr.ISocialPostBase) => void;
    post: Spintr.ISocialPostBase;
}

const Visage2ComposerTargetSelector = (props: IProps) => {
    const enableCompanyLevel = useSelector((state: Spintr.AppState) => state.instance.get("enableCompanyLevel"));
    const [targets, setTargets] = React.useState(props.post.targets);
    const dispatch = useDispatch();

    const dismiss = () => {
        if (
            targets.some((target) => !props.post.targets.some((postTarget) => postTarget.id === target.id)) ||
            props.post.targets.some((postTarget) => !targets.some((target) => target.id === postTarget.id))
        ) {
            dispatch(
                setConfirmPopup({
                    isOpen: true,
                    message: localize("UnsavedChangesWarning"),
                    onConfirm: props.onDismiss,
                })
            );

            return;
        }

        props.onDismiss();
    };

    return (
        <Modal
            isOpen={true}
            onDismiss={dismiss}
            isBlocking={true}
            containerClassName="Visage2ComposerContainer"
            className="Visage2Composer spintr-modal modalWithPopupHeader"
        >
            <PopupHeader text={localize("SynligFor")} displayBackButton onBackClick={dismiss} hideCloseButton />
            <div className="Visage2ComposerTargetSelector">
                <FormTokenizedObjectInput
                    showAllWhenEmpty
                    types={[
                        SpintrTypes.UberType.TargetGroup,
                        SpintrTypes.UberType.Role,
                        SpintrTypes.UberType.Department,
                        SpintrTypes.UberType.Office,
                        ...(enableCompanyLevel ? [SpintrTypes.UberType.Company] : []),
                    ]}
                    items={targets.map((target) => ({
                        ...target,
                        key: target.id,
                    }))}
                    onChange={(targets) => {
                        setTargets(
                            targets.map((target) => {
                                const nameParts = target.name.split(" > ");
                                return {
                                    ...target,
                                    name: nameParts[nameParts.length - 1],
                                    id: target.id || target.key,
                                    key: target.id || target.key,
                                    info: target.subText,
                                };
                            })
                        );
                    }}
                    label={localize("RiktadTillBeskrivning")}
                />

                <RecommendedAudiences
                    onAudienceSelected={(audience) => setTargets(onRecommendedAudienceSelected(targets, audience))}
                    targetIds={targets.map((target) => target.id)}
                />

                <Stack horizontal tokens={{ childrenGap: 6 }} horizontalAlign="end">
                    <DefaultButton text={localize("Avbryt")} onClick={props.onDismiss} />

                    <PrimaryButton
                        text={localize("Spara")}
                        onClick={() => {
                            props.onPostUpdate({
                                ...props.post,
                                targets,
                            });

                            props.onDismiss();
                        }}
                    />
                </Stack>
            </div>
        </Modal>
    );
};

export default Visage2ComposerTargetSelector;
